import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthenticatedAxios } from "../../utils/api";
import Header from "../../components/Header";
import LoadingLogo from "../../components/LoadingLogo";
import {
  Search,
  Edit3,
  Save,
  X,
  RefreshCw,
  RotateCcw,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";

const UsageLimits = () => {
  const navigate = useNavigate();
  const [usageData, setUsageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [newLimit, setNewLimit] = useState("");
  const [filter, setFilter] = useState("");
  const [sortBy, setSortBy] = useState("usage_percentage");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    fetchUsageData();
  }, []);

  const fetchUsageData = async () => {
    try {
      setLoading(true);
      const axios = getAuthenticatedAxios();
      const response = await axios.get("/api/v1/admin/usage-limits");
      if (response.data.success) {
        setUsageData(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch usage data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLimit = async (userId) => {
    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.put(
        `/api/v1/admin/users/${userId}/limit?limit=${newLimit}`,
      );
      if (response.data.success) {
        const updatedUsage = response.data.data || {};
        // Update local data
        setUsageData((prev) =>
          prev.map((user) =>
            user.user_id === userId
              ? {
                  ...user,
                  limit: parseInt(newLimit),
                  next_reset_date:
                    updatedUsage.next_reset_date ?? user.next_reset_date,
                }
              : user,
          ),
        );
        setEditingUser(null);
        setNewLimit("");
      }
    } catch (error) {
      console.error("Failed to update limit:", error);
      alert("Failed to update usage limit");
    }
  };

  const handleResetUsage = async (userId, email) => {
    const confirmed = window.confirm(
      `Reset current usage for ${email} to 0?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.post(
        `/api/v1/admin/users/${userId}/reset-usage`,
      );

      if (response.data.success) {
        const resetDate = response.data.data?.reset_date || null;
        const nextResetDate = response.data.data?.next_reset_date || null;
        setUsageData((prev) =>
          prev.map((user) =>
            user.user_id === userId
              ? {
                  ...user,
                  current_usage: 0,
                  usage_percentage: 0,
                  reset_date: resetDate,
                  next_reset_date: nextResetDate,
                }
              : user,
          ),
        );
      }
    } catch (error) {
      console.error("Failed to reset usage:", error);
      alert("Failed to reset user usage");
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const filteredAndSortedData = usageData
    .filter(
      (user) =>
        user.email?.toLowerCase().includes(filter.toLowerCase()) ||
        user.company_name?.toLowerCase().includes(filter.toLowerCase()),
    )
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return "text-red-600 bg-red-50";
    if (percentage >= 75) return "text-orange-600 bg-orange-50";
    if (percentage >= 50) return "text-yellow-600 bg-yellow-50";
    return "text-green-600 bg-green-50";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingLogo size="md" message="Loading usage data..." />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Usage Limits Management
                </h1>
                <p className="text-gray-600 mt-1">
                  Monitor and manage user search limits
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={fetchUsageData}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </button>
                <button
                  onClick={() => navigate("/admin")}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </button>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="text-sm text-gray-600">Total Users</div>
                <div className="text-2xl font-bold text-gray-900">
                  {usageData.length}
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="text-sm text-gray-600">At Limit</div>
                <div className="text-2xl font-bold text-red-600">
                  {usageData.filter((u) => u.usage_percentage >= 100).length}
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="text-sm text-gray-600">Near Limit (>75%)</div>
                <div className="text-2xl font-bold text-orange-600">
                  {
                    usageData.filter(
                      (u) =>
                        u.usage_percentage >= 75 && u.usage_percentage < 100,
                    ).length
                  }
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="text-sm text-gray-600">Avg Usage</div>
                <div className="text-2xl font-bold text-blue-600">
                  {usageData.length > 0
                    ? Math.round(
                        usageData.reduce(
                          (acc, u) => acc + u.usage_percentage,
                          0,
                        ) / usageData.length,
                      )
                    : 0}
                  %
                </div>
              </div>
            </div>

            {/* Filter */}
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center space-x-4">
                <Search className="h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by email or company..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("email")}
                      >
                        User
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("company_name")}
                      >
                        Company
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("current_usage")}
                      >
                        Usage
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("limit")}
                      >
                        Limit
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort("usage_percentage")}
                      >
                        Usage %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Next Reset Date
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAndSortedData.map((user) => (
                      <tr key={user.user_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900">
                              {user.email}
                            </div>
                            {user.role === "demo" && (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                                Demo
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {user.company_name || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {user.current_usage}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingUser === user.user_id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                value={newLimit}
                                onChange={(e) => setNewLimit(e.target.value)}
                                className="w-20 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                min="0"
                              />
                              <button
                                onClick={() => handleUpdateLimit(user.user_id)}
                                className="text-green-600 hover:text-green-700"
                              >
                                <Save className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingUser(null);
                                  setNewLimit("");
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="text-sm font-medium text-gray-900">
                              {user.limit}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUsageColor(
                              user.usage_percentage,
                            )}`}
                          >
                            {user.usage_percentage.toFixed(1)}%
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {user.next_reset_date
                              ? new Date(
                                  user.next_reset_date,
                                ).toLocaleDateString()
                              : "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-3">
                            {editingUser !== user.user_id && (
                              <button
                                onClick={() => {
                                  setEditingUser(user.user_id);
                                  setNewLimit(user.limit.toString());
                                }}
                                className="text-blue-600 hover:text-blue-700"
                                title="Edit limit"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() =>
                                handleResetUsage(user.user_id, user.email)
                              }
                              className="text-orange-600 hover:text-orange-700"
                              title="Reset usage to 0"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {filteredAndSortedData.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No users found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageLimits;
